import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Button,
  TabContent,
  TabPane,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import TextField from "@material-ui/core/TextField";
import BackgroundSlider from "react-background-slider";
import ReCAPTCHA from "react-google-recaptcha";
import OTPInput from "react-otp-input";
import image1 from "../assets/images/Carousel/BG-01.jpg";
import image2 from "../assets/images/Carousel/BG-02.jpg";
import image3 from "../assets/images/Carousel/BG-03.jpg";
import logo from "../assets/images/logo/login.png";
import api from "components/api/api";

const isDev = process.env.NODE_ENV === "development";
const otpLength = 6;
const DUMMY_VALID_OTP = "123456";

export default function Mongologin() {
  const history = useHistory();
  const isMountedRef = useRef(true);
  const resendTimerRef = useRef(null);
  const captchaRef = useRef(null);

  const [staffId, setStaffId] = useState(isDev ? "67741" : "");
  const [staffIdError, setStaffIdError] = useState("");

  const [otpui, setOtpui] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [hide, setHide] = useState(true);

  const [captchaToken, setCaptchaToken] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (resendTimerRef.current) {
        clearTimeout(resendTimerRef.current);
        resendTimerRef.current = null;
      }
    };
  }, []);

  const validateStaffId = (v) => {
    if (!v) return "Staff ID is required";
    if (v.length < 3 || v.length > 20) return "Must be 3–20 characters";
    if (!/^[a-zA-Z0-9]+$/.test(v)) return "Alphanumeric only";
    return "";
  };

  const handleStaffIdChange = (e) => {
    const value = e.target.value;
    setStaffId(value);
    setStaffIdError(validateStaffId(value));
  };

  // Dummy "Send OTP" - no API call, just moves to OTP screen (gated by captcha now)
  const handleSendOtp = () => {
    const err = validateStaffId(staffId);
    if (err) {
      setStaffIdError(err);
      return;
    }

    if (!captchaToken) {
      Swal.fire({
        icon: "error",
        text: "Please complete the captcha",
        timer: 1600,
      });
      return;
    }

    Swal.fire({
      icon: "success",
      text: "OTP Sent",
      timer: 1600,
      didClose: () => {
        if (isMountedRef.current) {
          setOtpui(true);
        }
      },
    });

    resendTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setHide(false);
    }, 60000);
  };

  const handleResendOtp = () => {
    setOtp("");
    setOtpError("");
    Swal.fire({
      icon: "success",
      text: "OTP Resent",
      timer: 1600,
    });

    setHide(true);
    resendTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setHide(false);
    }, 60000);
  };

  const handleBack = () => {
    setOtpui(false);
    setOtp("");
    setOtpError("");
    setHide(true);

    // Reset captcha so user has to solve it again if they go back
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
    setCaptchaToken(false);

    if (resendTimerRef.current) {
      clearTimeout(resendTimerRef.current);
      resendTimerRef.current = null;
    }
  };

  const validateOtp = (otpValue) => {
    setOtp(otpValue);
    if (otpValue.length === otpLength && otpValue !== DUMMY_VALID_OTP) {
      setOtpError("Invalid OTP");
    } else {
      setOtpError("");
    }
  };

  // Your original mongoLogin logic, untouched — just gated behind OTP now
  const handleLogin = async () => {
    if (otp !== DUMMY_VALID_OTP) {
      setOtpError("Invalid OTP");
      return;
    }

    setLoading(true);
    try {
      const res = await api.mongoLogin(staffId);
      if (!isMountedRef.current) return;

      const data = res.data?.data;
      if (res.data?.success && data) {
        // Store user session
        localStorage.setItem("token", data.clientId);
        localStorage.setItem("clientId", JSON.stringify(data.clientId));
        localStorage.setItem("userId", data.clientId);
        localStorage.setItem("username", data.staff_name);
        localStorage.setItem("is_admin", data._admin ? "true" : "false");
        localStorage.setItem("userRole", data.userRole);
        localStorage.setItem("Name", data.staff_name);
        localStorage.setItem("Outlet", data.staff_outlet);
        localStorage.setItem("Region", data.staff_region);
        localStorage.setItem("Position", JSON.stringify(data.staff_position));

        history.replace(`${process.env.PUBLIC_URL}/quiz-survey`);
      } else {
        Swal.fire({
          icon: "error",
          text: res.data?.message || "Login failed",
          timer: 2500,
        });
      }
    } catch (e) {
      if (!isMountedRef.current) return;
      // Server returns error message in e.data.message (from api.js interceptor)
      const msg = e?.data?.message || e?.message || "User not found";
      Swal.fire({ icon: "error", text: msg, timer: 2500 });
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  // Auto-submit when OTP is complete and valid (matches Logins.js UX)
  useEffect(() => {
    if (otp.length === otpLength && otp === DUMMY_VALID_OTP && !loading) {
      handleLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <Container fluid={true} className="p-0">
      <div
        className="BackgroundSlider"
        style={{ position: "fixed", width: "100%", height: "100vh" }}
      >
        <BackgroundSlider
          images={[image1, image2, image3]}
          duration={10}
          transition={2}
        />
      </div>
      <Row>
        <Col xs="12">
          <div className="login-card">
            <div>
              <div className="login-main login-tab">
                <Row>
                  <Col
                    xs="12"
                    md="6"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-around",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <img src={logo} alt="Logo" />
                    </div>
                    <div>
                      <p>Welcome to Omantel SIP</p>
                    </div>
                  </Col>
                  <Col xs="12" md="6">
                    <TabContent activeTab="jwt" className="content-login">
                      <TabPane className="fade show" tabId="jwt">
                        <Form className="theme-form">
                          <h4>Sign In</h4>

                          <FormGroup className="stafIdError">
                            <Label className="col-form-label">Staff ID</Label>
                            <TextField
                              variant="outlined"
                              className="form-control validationInputFed"
                              type="text"
                              value={staffId}
                              disabled={otpui}
                              onChange={handleStaffIdChange}
                              error={!!staffIdError}
                              id="standard-error-helper-text"
                              helperText={staffIdError || ""}
                              autoComplete="off"
                              inputProps={{ maxLength: 20 }}
                            />
                          </FormGroup>

                          {otpui && (
                            <FormGroup
                              style={{ marginTop: -20 }}
                              className="stafIdError"
                            >
                              <Label className="col-form-label">OTP</Label>
                              <OTPInput
                                onChange={validateOtp}
                                value={otp}
                                inputStyle="otp-input"
                                numInputs={6}
                                separator={<span></span>}
                                isInputSecure={true}
                                shouldAutoFocus={true}
                              />
                              {otpError && (
                                <div
                                  style={{
                                    color: "#dc3545",
                                    fontSize: 12,
                                    marginTop: 6,
                                  }}
                                >
                                  {otpError}
                                </div>
                              )}
                            </FormGroup>
                          )}

                          {!otpui && (
                            <div className="mb-3" style={{ marginTop: -10 }}>
                              <ReCAPTCHA
                                onChange={(token) => setCaptchaToken(token)}
                                onExpired={() => setCaptchaToken(false)}
                                sitekey="6Lc61GciAAAAAALcJjbHg364YaqPKyJ3_8jbj_Ks"
                                ref={captchaRef}
                              />
                            </div>
                          )}

                          <div className="form-group mb-0">
                            {otpui ? (
                              <Button
                                color="primary"
                                className="btn-block"
                                onClick={handleLogin}
                                disabled={otp.length !== otpLength || loading}
                              >
                                {loading ? "Signing In..." : "Sign In"}
                              </Button>
                            ) : (
                              <Button
                                color="primary"
                                className="btn-block"
                                onClick={handleSendOtp}
                                disabled={
                                  !staffId || !!staffIdError || !captchaToken
                                }
                              >
                                Send OTP
                              </Button>
                            )}
                          </div>

                          {otpui && (
                            <p
                              className="mt-4 mb-0"
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              {!hide ? (
                                <a
                                  className="ml-2 onHoverRegister"
                                  onClick={handleResendOtp}
                                  style={{ cursor: "pointer" }}
                                >
                                  {"Resend OTP"}
                                </a>
                              ) : (
                                <a
                                  className="ml-2 onHoverRegister"
                                  style={{
                                    color: "#ccc",
                                    cursor: "not-allowed",
                                  }}
                                >
                                  {"Wait for 60s to resend OTP"}
                                </a>
                              )}

                              <a
                                className="ml-2 onHoverRegister"
                                onClick={handleBack}
                                style={{ cursor: "pointer" }}
                              >
                                {"Back"}
                              </a>
                            </p>
                          )}
                        </Form>
                      </TabPane>
                    </TabContent>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
